import React, { useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  fetchStatus,
  onChangeSaveModalPreference,
  onChangeTabValues,
} from "../../store/slice/preferenceSlice";
import { checkIsMappingsSaved } from "../../store/slice/mappingSlice";
import { checkCountForProductMapped } from "../invoice/SyncRules";
import { onSaveContactSyncRules } from "../contact/SyncRules";
import { onSaveContactDefaultMappings } from "../contact/DefaultMappings";
import { onSaveInvoiceSyncRules } from "../invoice/SyncRules";
import { onSaveMappings } from "../mapping/MappingWindow";

// CMS Helpers
import { useCmsHook } from "../../helpers/cms/cmsHook";

function InfoModal() {
  const { cmsInstance } = useCmsHook();

  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  // preference states
  const {
    saveModalPreference,
    settingsActiveTab,
    contactActiveTab,
    invoiceActiveTab,
  } = useSelector((state) => state.preference);
  // contact states
  const {
    isContactSyncRulesSaved,
    isContactDefaultMappingsSaved,
    stripeFilterPreference,
    msbcFilterPreference,
    stripeContactMetadataPreference,
    stripeCustomerNumberPreference,
    stripeCustomerPreference,
    paymentTermsCodePreference,
    currencyCodePreference,
    customerPostingGroupPreference,
    vatBusinessPostingGroupPreference,
    generalBusinessPostingGroupPreference,
  } = useSelector((state) => state.contact);
  // invoice states
  const {
    isInvoiceSyncRulesSaved,
    preferenceForProduct,
    productPreference,
    stripeProductSearchParameter,
    msbcProductSearchParameter,
    stripeProductMetadataPreference,
    preferenceForSearchParameters,
    invoiceTriggerPreference,
  } = useSelector((state) => state.invoice);
  // mapping states
  const { isMappingsSaved, mappings } = useSelector((state) => state.mapping);

  /**
   * This function is used for saving the unsaved changes
   */
  const onSaveUnsavedFields = async () => {
    if (settingsActiveTab === "contact") {
      if (contactActiveTab === "syncRules" && !isContactSyncRulesSaved) {
        await onSaveContactSyncRules({
          setIsLoading,
          searchParams,
          stripeFilterPreference,
          msbcFilterPreference,
          stripeContactMetadataPreference,
          stripeCustomerNumberPreference,
          stripeCustomerPreference,
          dispatch,
          cmsInstance,
        });
      } else if (
        contactActiveTab === "defaultMappings" &&
        !isContactDefaultMappingsSaved
      ) {
        await onSaveContactDefaultMappings({
          setIsLoading,
          searchParams,
          paymentTermsCodePreference,
          currencyCodePreference,
          customerPostingGroupPreference,
          vatBusinessPostingGroupPreference,
          generalBusinessPostingGroupPreference,
          dispatch,
          cmsInstance,
        });
      } else if (
        contactActiveTab === "fieldMappings" &&
        !isMappingsSaved.customer
      ) {
        await onSaveMappings({
          dispatch,
          setIsButtonLoading: setIsLoading,
          mappings: mappings.mappings.customer,
          mappingsFor: "customer",
          searchParams,
          cmsInstance,
          notificationPath: "contactFieldMappings",
        });
      }
    } else if (settingsActiveTab === "invoice") {
      if (invoiceActiveTab === "syncRules" && !isInvoiceSyncRulesSaved) {
        await onSaveInvoiceSyncRules({
          setIsLoading,
          searchParams,
          preferenceForProduct,
          productPreference,
          stripeProductSearchParameter,
          msbcProductSearchParameter,
          stripeProductMetadataPreference,
          preferenceForSearchParameters,
          dispatch,
          invoiceTriggerPreference,
          cmsInstance,
        });

        if (
          preferenceForSearchParameters === "Set custom mappings" &&
          checkCountForProductMapped({ mappings: mappings.mappings.product }) >
            0 &&
          !isMappingsSaved.product
        ) {
          await onSaveMappings({
            dispatch,
            setIsButtonLoading: setIsLoading,
            mappings: mappings.mappings.product,
            mappingsFor: "product",
            searchParams,
            cmsInstance,
            notificationPath: "productFieldMappings",
            preferenceForProduct,
            productPreference,
            stripeProductSearchParameter,
            stripeProductMetadataPreference,
            msbcProductSearchParameter,
            preferenceForSearchParameters,
          });
        }
      } else if (
        invoiceActiveTab === "fieldMappings" &&
        !isMappingsSaved.invoice
      ) {
        await onSaveMappings({
          dispatch,
          setIsButtonLoading: setIsLoading,
          mappings: mappings.mappings.invoice,
          mappingsFor: "invoice",
          searchParams,
          cmsInstance,
          notificationPath: "invoiceFieldMappings",
        });
      }
    }
    dispatch(onChangeSaveModalPreference({ show: false }));
    dispatch(onChangeTabValues());
  };

  /**
   * This function is responsible for getting the saved preferences and mappings
   */
  const onDiscardChanges = () => {
    const account = searchParams.get("account");

    dispatch(fetchStatus(account));
    dispatch(checkIsMappingsSaved({ mappingsFor: "customer" }));
    dispatch(checkIsMappingsSaved({ mappingsFor: "invoice" }));
    dispatch(checkIsMappingsSaved({ mappingsFor: "product" }));
    dispatch(onChangeSaveModalPreference({ show: false }));
    dispatch(onChangeTabValues());
  };

  const renderContent = () => {
    if (
      settingsActiveTab === "invoice" &&
      invoiceActiveTab === "syncRules" &&
      preferenceForSearchParameters === "Set custom mappings" &&
      checkCountForProductMapped({ mappings: mappings.mappings.product }) === 0
    ) {
      return (
        <div>
          <p className="rules-description">
            {cmsInstance &&
              cmsInstance.sendData().cmsContent.notifications.autoSave
                .successContent.productMappings.message}
          </p>
          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, 100);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else if (
      settingsActiveTab === "contact" &&
      contactActiveTab === "syncRules" &&
      stripeFilterPreference &&
      stripeFilterPreference.value === "key" &&
      !stripeContactMetadataPreference
    ) {
      return (
        <div>
          <p className="rules-description">
            {cmsInstance &&
              cmsInstance.sendData().cmsContent.notifications.autoSave
                .successContent.contactMetadata.message}
          </p>
          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, 0);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else if (
      settingsActiveTab === "invoice" &&
      invoiceActiveTab === "syncRules" &&
      stripeProductSearchParameter &&
      stripeProductSearchParameter.value === "key" &&
      !stripeProductMetadataPreference
    ) {
      return (
        <div>
          <p className="rules-description">
            {cmsInstance &&
              cmsInstance.sendData().cmsContent.notifications.autoSave
                .successContent.invoiceMetadata.message}
          </p>
          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, 0);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else if (
      settingsActiveTab === "invoice" &&
      invoiceActiveTab === "syncRules" &&
      !invoiceTriggerPreference
    ) {
      return (
        <div>
          <p className="rules-description">
            {cmsInstance &&
              cmsInstance.sendData().cmsContent.notifications.autoSave
                .successContent.stripeTrigger.message}
          </p>
          <div className="d-flex flex-row justify-content-end">
            <Button
              className="save-changes-button"
              onClick={() => {
                dispatch(onChangeSaveModalPreference({ show: false }));
                window.scrollTo(0, document.body.scrollHeight);
              }}
            >
              Edit
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <p className="rules-description">
            {cmsInstance &&
              cmsInstance.sendData().cmsContent.notifications.autoSave
                .successContent.unsavedChanges.message}
          </p>
          <div className="d-flex flex-row justify-content-end">
            <Button
              className="discard-modal-button me-3"
              onClick={() => onDiscardChanges()}
              // disabled={loading}
            >
              Discard
            </Button>
            <Button
              className="save-changes-button"
              onClick={() => onSaveUnsavedFields()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <span>Save changes</span>
              )}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <Modal
      show={saveModalPreference}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
}

export default InfoModal;
